export const contactSchema = {
  fields: {
    name: {
      label: "Name",
      required: true,
      minLength: 2,
      maxLength: 120
    },
    email: {
      label: "Email",
      required: true,
      maxLength: 160,
      pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$"
    },
    company: {
      label: "Company",
      required: false,
      maxLength: 160
    },
    phone: {
      label: "Phone",
      required: false,
      maxLength: 40,
      pattern: "^[+()\\-\\s\\d.]{7,40}$"
    },
    projectType: {
      label: "Project Type",
      required: true,
      options: ["custom-fabrication", "products", "finishing", "design-assist"]
    },
    message: {
      label: "Scope Notes",
      required: true,
      minLength: 20,
      maxLength: 4000
    }
  }
} as const;

export type ContactField = keyof typeof contactSchema.fields;
export type ContactPayload = Record<ContactField, string>;
export type ContactErrors = Partial<Record<ContactField, string>>;

const emptyPayload: ContactPayload = {
  name: "",
  email: "",
  company: "",
  phone: "",
  projectType: "",
  message: ""
};

export function validateContactPayload(input: unknown) {
  const payload = normalizePayload(input);
  const errors: ContactErrors = {};

  for (const field of Object.keys(contactSchema.fields) as ContactField[]) {
    const rule = contactSchema.fields[field];
    const value = payload[field];

    if (rule.required && !value) {
      errors[field] = `${rule.label} is required.`;
      continue;
    }

    if ("minLength" in rule && value && value.length < rule.minLength) {
      errors[field] = `${rule.label} must be at least ${rule.minLength} characters.`;
      continue;
    }

    if ("maxLength" in rule && value.length > rule.maxLength) {
      errors[field] = `${rule.label} must be ${rule.maxLength} characters or fewer.`;
      continue;
    }

    if ("pattern" in rule && value && !new RegExp(rule.pattern).test(value)) {
      errors[field] = `${rule.label} is not valid.`;
      continue;
    }

    if ("options" in rule && !rule.options.includes(value as never)) {
      errors[field] = `Select a valid ${rule.label.toLowerCase()}.`;
    }
  }

  return {
    data: payload,
    errors,
    valid: Object.keys(errors).length === 0
  };
}

function normalizePayload(input: unknown): ContactPayload {
  const source = isRecord(input) ? input : {};

  return (Object.keys(emptyPayload) as ContactField[]).reduce<ContactPayload>(
    (next, field) => {
      const value = source[field];
      next[field] = typeof value === "string" ? value.trim() : "";
      return next;
    },
    { ...emptyPayload }
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
